// components/ClanCrewCreateCohortModal.jsx
// Create new cohorts with filters for state, city, designation, performance band
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Modal, Input, Select, Card, Checkbox, Tag, message, Spin, Empty } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Users, Filter, Save, Search, List, ChevronDown, ChevronUp, MapPin, Briefcase, TrendingUp, UserCheck } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { GET_COHORT_FILTER_OPTIONS, CREATE_COHORT, GET_User_Details_User_Management_For_ClanCrew, GET_LIST_OF_COHORTS, Export_Filtered_Users_For_Cohort } from '../../services/userAPI';
import './clanCrewCreateCohortModal.css';

const { Option } = Select;

// Performance band options (static - no API needed)
const perfMap = {
    HIGH: 'High',
    MEDIUM: 'Medium',
    LOW: 'Low',
};

const PERFORMANCE_BANDS = [
    { value: 'HIGH', label: 'High' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'LOW', label: 'Low' },
];

const ClanCrewCreateCohortModal = ({ visible, onCancel, onCohortCreated, accountId }) => {
    const location = useLocation();
    const roleId = location.state?.roleId;

    // Tab state - 'create' or 'view'
    const [activeTab, setActiveTab] = useState('create');
    const [expandedCohortId, setExpandedCohortId] = useState(null);

    // Form state
    const [cohortName, setCohortName] = useState('');
    const [filters, setFilters] = useState({
        state: '',
        stateId: null,
        city: '',
        cityId: null,
        designation: '',
        designationId: null,
        performanceBand: '',
        userTypeId: null,
        userType: '',
    });
    const [searchName, setSearchName] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [unselectedUserIds, setUnselectedUserIds] = useState([]);
    const [autoSelectedOnce, setAutoSelectedOnce] = useState(false);
    const [createdCohortId, setCreatedCohortId] = useState(null);

    // API Queries - Dependent dropdown filter options
    const [fetchFilterOptions, { data: filterOptionsData, loading: filterOptionsLoading }] = useLazyQuery(GET_COHORT_FILTER_OPTIONS, {
        fetchPolicy: 'network-only',
    });

    const [fetchUsers, { data: usersData, loading: usersLoading, fetchMore }] = useLazyQuery(
        GET_User_Details_User_Management_For_ClanCrew,
        { fetchPolicy: 'network-only' }
    );

    const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);
    const isAppendingRef = useRef(false);

    const [createCohort, { loading: creatingCohort }] = useMutation(CREATE_COHORT);

    const [exportCohortUsers, { loading: exportCohortLoading }] = useLazyQuery(Export_Filtered_Users_For_Cohort, {
        fetchPolicy: 'network-only',
    });

    // Fetch existing cohorts
    const { data: cohortsData, loading: cohortsLoading, refetch: refetchCohorts } = useQuery(GET_LIST_OF_COHORTS, {
        variables: { accountId: accountId ? parseInt(accountId) : null, jobroleId: roleId ? parseInt(roleId) : null },
        skip: !accountId && !roleId,
        fetchPolicy: 'network-only',
    });

    const cohorts = useMemo(() => {
        return cohortsData?.getListOfCohorts || [];
    }, [cohortsData]);

    // Build options from dependent filter API data
    const filterData = filterOptionsData?.getCohortFilterOptions;

    const stateOptions = useMemo(() => {
        const options = filterData?.states || [];
        if (filters.stateId && filters.state && !options.find(s => s.id === filters.stateId)) {
            return [{ id: filters.stateId, stateName: filters.state }, ...options];
        }
        return options;
    }, [filterData, filters.stateId, filters.state]);

    const cityOptions = useMemo(() => {
        const options = filterData?.cities || [];
        if (filters.cityId && filters.city && !options.find(c => c.id === filters.cityId)) {
            return [{ id: filters.cityId, cityName: filters.city }, ...options];
        }
        return options;
    }, [filterData, filters.cityId, filters.city]);

    const designationOptions = useMemo(() => {
        const options = filterData?.designations || [];
        if (filters.designationId && filters.designation && !options.find(d => d.id === filters.designationId)) {
            return [{ id: filters.designationId, designation: filters.designation }, ...options];
        }
        return options;
    }, [filterData, filters.designationId, filters.designation]);

    const userTypeOptions = useMemo(() => {
        const options = filterData?.userTypes || [];
        if (filters.userTypeId && filters.userType && !options.find(u => u.id === filters.userTypeId)) {
            return [{ id: filters.userTypeId, userType: filters.userType }, ...options];
        }
        return options;
    }, [filterData, filters.userTypeId, filters.userType]);

    // Users from API
    const users = useMemo(() => {
        return usersData?.getUsersForAccount?.data || [];
    }, [usersData]);

    const pageMeta = useMemo(() => {
        return usersData?.getUsersForAccount?.pageMeta || null;
    }, [usersData]);

    const totalUsersCount = pageMeta?.totalCount ?? users.length;

    const buildGqlFilters = useCallback(() => {
        const gqlFilters = [];
        if (filters.state) gqlFilters.push({ field: "state", value: filters.state });
        if (filters.city) gqlFilters.push({ field: "city", value: filters.city });
        if (filters.designation) gqlFilters.push({ field: "designation", value: filters.designation });
        if (filters.performanceBand) {
            const perfKey = Object.keys(perfMap).find(k => perfMap[k] === filters.performanceBand);
            if (perfKey && perfKey !== 'UNKNOWN') {
                gqlFilters.push({ field: "performanceResidency.performance", value: perfKey });
            }
        }
        if (filters.userTypeId) {
            gqlFilters.push({ field: "userTypeId", value: filters.userTypeId });
        }
        return gqlFilters.length > 0 ? gqlFilters : null;
    }, [filters]);

    // Fetch users when modal opens or filters change
    const refetchUsers = useCallback(() => {
        if (!roleId) return;

        fetchUsers({
            variables: {
                roleId: parseInt(roleId),
                filterByName: searchName.trim() || undefined,
                filters: buildGqlFilters(),
            },
        });
    }, [fetchUsers, roleId, searchName, buildGqlFilters]);

    // Load next page of users (infinite scroll)
    const loadMoreUsers = useCallback(() => {
        if (!roleId || !fetchMore || !pageMeta?.hasNextPage || !pageMeta?.nextPage) return;
        if (loadingMoreUsers) return;

        isAppendingRef.current = true;
        setLoadingMoreUsers(true);
        fetchMore({
            variables: {
                roleId: parseInt(roleId),
                filterByName: searchName.trim() || undefined,
                filters: buildGqlFilters(),
                pagingCriteria: { cursor: pageMeta.nextPage },
            },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult?.getUsersForAccount) return prev;
                return {
                    ...prev,
                    getUsersForAccount: {
                        ...fetchMoreResult.getUsersForAccount,
                        data: [
                            ...(prev.getUsersForAccount?.data || []),
                            ...(fetchMoreResult.getUsersForAccount.data || []),
                        ],
                    },
                };
            },
        }).catch(() => {
            isAppendingRef.current = false;
        }).finally(() => {
            setLoadingMoreUsers(false);
        });
    }, [fetchMore, roleId, pageMeta, loadingMoreUsers, searchName, buildGqlFilters]);

    // Trigger next page load when scrolled 70% down the users list
    const handleUsersScroll = useCallback((e) => {
        const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
        if (scrollHeight <= 0) return;
        if ((scrollTop + clientHeight) / scrollHeight >= 0.7) {
            loadMoreUsers();
        }
    }, [loadMoreUsers]);

    useEffect(() => {
        if (visible && roleId) {
            refetchUsers();
        }
    }, [visible, roleId, filters, searchName, refetchUsers]);

    // Fetch dependent filter options when modal opens or any filter changes
    useEffect(() => {
        if (visible && roleId && accountId) {
            fetchFilterOptions({
                variables: {
                    roleId: parseInt(roleId),
                    accountId: parseInt(accountId),
                    stateId: filters.stateId ? parseInt(filters.stateId) : null,
                    cityId: filters.cityId ? parseInt(filters.cityId) : null,
                    designationId: filters.designationId ? parseInt(filters.designationId) : null,
                    userTypeId: filters.userTypeId ? parseInt(filters.userTypeId) : null,
                },
            });
        }
    }, [visible, roleId, accountId, filters.stateId, filters.cityId, filters.designationId, filters.userTypeId, fetchFilterOptions]);

    // Auto-select all active users when users load or change (after filter applied)
    useEffect(() => {
        if (users.length > 0) {
            const activeUserIds = users
                .filter(u => u.profileActivated)
                .map(u => u.id);
            if (isAppendingRef.current) {
                // Next page appended - preserve existing selections, auto-select newly loaded active users
                isAppendingRef.current = false;
                setSelectedUserIds(prev => {
                    const unselected = new Set(unselectedUserIds);
                    const merged = new Set(prev);
                    activeUserIds.forEach(id => {
                        if (!unselected.has(id)) merged.add(id);
                    });
                    return [...merged];
                });
            } else {
                setSelectedUserIds(activeUserIds);
                setUnselectedUserIds([]);
            }
            setAutoSelectedOnce(true);
        } else {
            // Clear selected users when no users match the filters
            setSelectedUserIds([]);
            setUnselectedUserIds([]);
        }
    }, [users]);

    const toggleUser = (userId) => {
        if (selectedUserIds.includes(userId)) {
            setSelectedUserIds(prev => prev.filter(id => id !== userId));
            setUnselectedUserIds(prev => prev.includes(userId) ? prev : [...prev, userId]);
        } else {
            setSelectedUserIds(prev => prev.includes(userId) ? prev : [...prev, userId]);
            setUnselectedUserIds(prev => prev.filter(id => id !== userId));
        }
    };

    const selectAllUsers = () => {
        const activeUserIds = users.filter(u => u.profileActivated).map(u => u.id);
        setSelectedUserIds(activeUserIds);
        setUnselectedUserIds([]);
    };

    const deselectAllUsers = () => {
        setSelectedUserIds([]);
        setUnselectedUserIds(users.filter(u => u.profileActivated).map(u => u.id));
    };

    const resetForm = () => {
        setCohortName('');
        setFilters({
            state: '',
            stateId: null,
            city: '',
            cityId: null,
            designation: '',
            designationId: null,
            performanceBand: '',
            userTypeId: null,
            userType: '',
        });
        setSearchName('');
        setSelectedUserIds([]);
        setUnselectedUserIds([]);
        setAutoSelectedOnce(false);
        setCreatedCohortId(null);
        setActiveTab('create');
        setExpandedCohortId(null);
    };

    const handleSaveCohort = async () => {
        if (!cohortName.trim()) {
            message.error('Please enter a cohort name');
            return;
        }

        if (selectedUserIds.length === 0) {
            message.error('Please select at least one user');
            return;
        }

        // Build cohort filters for API
        const cohortFilters = [];
        if (filters.state) {
            cohortFilters.push({ field: 'state', value: filters.state });
        }
        if (filters.city) {
            cohortFilters.push({ field: 'city', value: filters.city });
        }
        if (filters.designation) {
            cohortFilters.push({ field: 'designation', value: filters.designation });
        }
        if (filters.performanceBand) {
            const perfKey = Object.keys(perfMap).find(k => perfMap[k] === filters.performanceBand);
            if (perfKey) {
                cohortFilters.push({ field: 'performanceResidency.performance', value: perfKey });
            }
        }
        if (filters.userTypeId) {
            cohortFilters.push({ field: 'userTypeId', value: filters.userTypeId });
        }
        if (unselectedUserIds.length > 0) {
            cohortFilters.push({ field: 'unselectedUserIds', value: unselectedUserIds });
        }

        try {
            const response = await createCohort({
                variables: {
                    cohortName: cohortName,
                    accountId: accountId ? parseInt(accountId) : null,
                    jobroleId: roleId ? parseInt(roleId) : null,
                    filters: cohortFilters,
                },
            });

            if (response.data?.createCohort?.success) {
                message.success(`Cohort "${cohortName}" created successfully with ${selectedUserIds.length} users!`);
                refetchCohorts(); // Refresh cohorts list
                const newCohortId = response.data.createCohort.data?.id;
                setCreatedCohortId(newCohortId);

                if (onCohortCreated) {
                    onCohortCreated({
                        id: newCohortId,
                        name: cohortName,
                        filters: cohortFilters,
                        userCount: selectedUserIds.length,
                    });
                }
            } else {
                message.error(response.data?.createCohort?.message || 'Failed to create cohort. Please try again.');
            }
        } catch (error) {
            console.error('Error creating cohort:', error);
            message.error('Failed to create cohort. Please try again.');
        }
    };

    const handleDownloadCohortCSV = async (cohortId) => {
        try {
            const { data } = await exportCohortUsers({
                variables: {
                    accountId: accountId ? parseInt(accountId) : null,
                    jobroleId: roleId ? parseInt(roleId) : null,
                    cohortId: parseInt(cohortId),
                },
            });

            if (data?.exportFilteredUsersForCohort) {
                const fileUrl = data.exportFilteredUsersForCohort;
                const link = document.createElement('a');
                link.href = fileUrl;
                link.download = `cohort_users_${cohortId}_${new Date().toISOString().split('T')[0]}.csv`;
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                message.success('CSV downloaded successfully!');
            } else {
                message.error('Failed to download CSV. No data returned.');
            }
        } catch (error) {
            console.error('Error downloading cohort CSV:', error);
            message.error('Failed to download CSV. Please try again.');
        }
    };

    const hasFilters = filters.state || filters.city || filters.designation || filters.performanceBand || filters.userTypeId;

    // Helper function to parse and display filter info
    const getFilterDisplay = (filterArray) => {
        if (!filterArray || !Array.isArray(filterArray)) return [];
        return filterArray.filter(f => f.field !== 'unselectedUserIds').map(f => {
            let label = f.field;
            let value = f.value;

            if (f.field === 'state') label = 'State';
            else if (f.field === 'city') label = 'City';
            else if (f.field === 'designation') label = 'Designation';
            else if (f.field === 'performanceResidency.performance') {
                label = 'Performance';
                value = perfMap[f.value] || f.value;
            }
            else if (f.field === 'userTypeId') label = 'User Type';

            return { label, value };
        });
    };

    const getFilterIcon = (label) => {
        switch (label) {
            case 'State':
            case 'City':
                return <MapPin size={12} />;
            case 'Designation':
                return <Briefcase size={12} />;
            case 'Performance':
                return <TrendingUp size={12} />;
            case 'User Type':
                return <UserCheck size={12} />;
            default:
                return <Filter size={12} />;
        }
    };

    return (
        <Modal
            open={visible}
            onCancel={() => { onCancel(); resetForm(); }}
            footer={null}
            width={900}
            centered
            className="clan-crew-cohort-modal"
            title={
                <div className="flex items-center gap-2 font-bold">
                    {/* <Users size={20} /> */}
                    Cohort Management
                </div>
            }
        >
            {/* Tab Navigation */}
            <div className="flex gap-4 mb-6 border-b border-gray-200">
                <button
                    onClick={() => setActiveTab('create')}
                    className={`flex items-center gap-2 px-4 py-3 font-medium transition-all border-b-2 -mb-[1px] ${activeTab === 'create'
                        ? 'text-[#AD0101] border-[#AD0101]'
                        : 'text-gray-500 border-transparent hover:text-gray-700'
                        }`}
                >
                    <Users size={18} />
                    Create New Cohort
                </button>
                <button
                    onClick={() => setActiveTab('view')}
                    className={`flex items-center gap-2 px-4 py-3 font-medium transition-all border-b-2 -mb-[1px] ${activeTab === 'view'
                        ? 'text-[#AD0101] border-[#AD0101]'
                        : 'text-gray-500 border-transparent hover:text-gray-700'
                        }`}
                >
                    <List size={18} />
                    View Cohorts ({cohorts.length})
                </button>
            </div>

            {/* View Cohorts Tab */}
            {activeTab === 'view' && (
                <Spin spinning={cohortsLoading} indicator={<LoadingOutlined style={{ fontSize: 24, color: '#AD0101' }} spin />}>
                    <div className="cohort-view-list max-h-[600px] overflow-y-auto">
                        {cohorts.length === 0 ? (
                            <Empty description="No cohorts created yet" className="py-16" />
                        ) : (
                            <div className="space-y-3">
                                {cohorts.map((cohort) => {
                                    const isExpanded = expandedCohortId === cohort.id;
                                    const filtersList = getFilterDisplay(cohort.filters);

                                    return (
                                        <div
                                            key={cohort.id}
                                            className={`border-2 rounded-xl overflow-hidden transition-all duration-300 ${isExpanded
                                                ? 'border-[#AD0101] shadow-lg'
                                                : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                        >
                                            {/* Cohort Header */}
                                            <div
                                                className="cohort-view-header flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => setExpandedCohortId(isExpanded ? null : cohort.id)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#AD0101] to-[#9B0000] flex items-center justify-center text-white text-sm font-bold">
                                                        {cohort.cohortName?.charAt(0)?.toUpperCase() || 'C'}
                                                    </div>
                                                    <div>
                                                        <h3 className="font-semibold text-gray-800">{cohort.cohortName}</h3>
                                                        {cohort.description && (
                                                            <p className="text-sm text-gray-500">{cohort.description}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <div className="flex items-center gap-2">
                                                        <Tag color="blue" className="m-0">
                                                            <span className="flex items-center gap-1">
                                                                <Filter size={12} />
                                                                {filtersList.length} Filters
                                                            </span>
                                                        </Tag>
                                                    </div>
                                                    <div className="text-[#AD0101]">
                                                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Expanded Content */}
                                            {isExpanded && (
                                                <div className="px-3 pb-3 border-t border-gray-100 bg-gray-50">
                                                    <div className="pt-3">
                                                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                                            <Filter size={14} className="text-[#AD0101]" />
                                                            Applied Filters
                                                        </h4>
                                                        {filtersList.length === 0 ? (
                                                            <p className="text-sm text-gray-500 italic">No filters applied</p>
                                                        ) : (
                                                            <div className="flex flex-wrap gap-2">
                                                                {filtersList.map((filter, idx) => (
                                                                    <Tag
                                                                        key={idx}
                                                                        className="flex items-center gap-1 px-2 py-1"
                                                                        style={{
                                                                            backgroundColor: '#FEF2F2',
                                                                            color: '#AD0101',
                                                                            borderColor: '#FECACA',
                                                                            fontSize: '12px'
                                                                        }}
                                                                    >
                                                                        {getFilterIcon(filter.label)}
                                                                        <span className="font-medium">{filter.label}:</span>
                                                                        <span>{filter.value}</span>
                                                                    </Tag>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="mt-4 flex justify-end">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDownloadCohortCSV(cohort.id);
                                                            }}
                                                            disabled={exportCohortLoading}
                                                            className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium transition"
                                                            style={{
                                                                backgroundColor: exportCohortLoading ? '#999' : '#AD0101',
                                                                cursor: exportCohortLoading ? 'not-allowed' : 'pointer',
                                                                border: 'none',
                                                                fontSize: '13px',
                                                            }}
                                                        >
                                                            {exportCohortLoading
                                                                ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                                                : 'Download CSV'}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </Spin>
            )}

            {/* Create Cohort Tab */}
            {activeTab === 'create' && (
                <Spin spinning={usersLoading || creatingCohort} indicator={<LoadingOutlined style={{ fontSize: 24, color: '#AD0101' }} spin />}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Left Panel - Filters */}
                        <Card
                            title={
                                <span className="flex items-center gap-2">
                                    <Filter size={18} /> Cohort Filters
                                </span>
                            }
                            className="h-full"
                        >
                            {/* Cohort Name */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">
                                    Cohort Name <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    placeholder="Enter cohort name"
                                    value={cohortName}
                                    onChange={e => setCohortName(e.target.value)}
                                    size="middle"
                                />
                            </div>

                            {/* State Filter */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">State</label>
                                <Select
                                    placeholder="Select State"
                                    value={filters.state || undefined}
                                    onSelect={(val) => {
                                        const selectedState = stateOptions.find(s => s.stateName === val);
                                        setFilters(prev => ({
                                            ...prev,
                                            state: val || '',
                                            stateId: selectedState?.id || null,
                                            city: '',
                                            cityId: null,
                                        }));
                                        setAutoSelectedOnce(false);
                                    }}
                                    onClear={() => {
                                        setFilters(prev => ({
                                            ...prev,
                                            state: '',
                                            stateId: null,
                                            city: '',
                                            cityId: null,
                                        }));
                                        setAutoSelectedOnce(false);
                                    }}
                                    allowClear
                                    className="w-full"
                                    size="middle"
                                >
                                    {stateOptions.map(state => (
                                        <Option key={state.id} value={state.stateName}>{state.stateName}</Option>
                                    ))}
                                </Select>
                            </div>

                            {/* City Filter */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">City</label>
                                <Select
                                    placeholder="Select City"
                                    value={filters.city || undefined}
                                    onSelect={(val) => {
                                        const selectedCity = cityOptions.find(c => c.cityName === val);
                                        setFilters(prev => ({
                                            ...prev,
                                            city: val || '',
                                            cityId: selectedCity?.id || null,
                                        }));
                                        setAutoSelectedOnce(false);
                                    }}
                                    onClear={() => {
                                        setFilters(prev => ({
                                            ...prev,
                                            city: '',
                                            cityId: null,
                                        }));
                                        setAutoSelectedOnce(false);
                                    }}
                                    allowClear
                                    disabled={!filters.stateId}
                                    className="w-full"
                                    size="middle"
                                >
                                    {cityOptions.map(city => (
                                        <Option key={city.id} value={city.cityName}>{city.cityName}</Option>
                                    ))}
                                </Select>
                            </div>

                            {/* Designation Filter */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Designation</label>
                                <Select
                                    placeholder="Select Designation"
                                    value={filters.designation || undefined}
                                    onSelect={(val) => {
                                        const selectedDesignation = designationOptions.find(d => d.designation === val);
                                        setFilters(prev => ({
                                            ...prev,
                                            designation: val || '',
                                            designationId: selectedDesignation?.id || null,
                                        }));
                                        setAutoSelectedOnce(false);
                                    }}
                                    onClear={() => {
                                        setFilters(prev => ({
                                            ...prev,
                                            designation: '',
                                            designationId: null,
                                        }));
                                        setAutoSelectedOnce(false);
                                    }}
                                    allowClear
                                    className="w-full"
                                    size="middle"
                                >
                                    {designationOptions.map(d => (
                                        <Option key={d.id} value={d.designation}>{d.designation}</Option>
                                    ))}
                                </Select>
                            </div>

                            {/* Performance Band Filter */}
                            {/* <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Performance Band</label>
                                <Select
                                    placeholder="Select Performance Band"
                                    value={filters.performanceBand || undefined}
                                    onChange={(val) => {
                                        setFilters(prev => ({ ...prev, performanceBand: val || '' }));
                                        setAutoSelectedOnce(false);
                                    }}
                                    allowClear
                                    className="w-full"
                                    size="middle"
                                >
                                    {PERFORMANCE_BANDS.map(p => (
                                        <Option key={p.value} value={p.label}>{p.label}</Option>
                                    ))}
                                </Select>
                            </div> */}

                            {/* User Type Filter */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">User Type</label>
                                <Select
                                    placeholder="Select User Type"
                                    value={filters.userTypeId || undefined}
                                    onSelect={(val) => {
                                        const selectedUserType = userTypeOptions.find(u => u.id === val);
                                        setFilters(prev => ({
                                            ...prev,
                                            userTypeId: val || null,
                                            userType: selectedUserType?.userType || '',
                                        }));
                                        setAutoSelectedOnce(false);
                                    }}
                                    onClear={() => {
                                        setFilters(prev => ({
                                            ...prev,
                                            userTypeId: null,
                                            userType: '',
                                        }));
                                        setAutoSelectedOnce(false);
                                    }}
                                    allowClear
                                    className="w-full"
                                    size="middle"
                                >
                                    {userTypeOptions.map(ut => (
                                        <Option key={ut.id} value={ut.id}>{ut.userType}</Option>
                                    ))}
                                </Select>
                            </div>

                            {/* Active Filters Display */}
                            {hasFilters && (
                                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                    <div className="text-sm font-medium mb-2">Active Filters:</div>
                                    <div className="flex flex-wrap gap-2">
                                        {filters.state && (
                                            <Tag closable onClose={() => setFilters(prev => ({ ...prev, state: '', stateId: null, city: '', cityId: null }))} className="bg-red-50" style={{ color: '#AD0101', borderColor: '#AD0101' }}>
                                                State: {filters.state}
                                            </Tag>
                                        )}
                                        {filters.city && (
                                            <Tag closable onClose={() => setFilters(prev => ({ ...prev, city: '', cityId: null }))} className="bg-red-50" style={{ color: '#AD0101', borderColor: '#AD0101' }}>
                                                City: {filters.city}
                                            </Tag>
                                        )}
                                        {filters.designation && (
                                            <Tag closable onClose={() => setFilters(prev => ({ ...prev, designation: '', designationId: null }))} className="bg-red-50" style={{ color: '#AD0101', borderColor: '#AD0101' }}>
                                                Designation: {filters.designation}
                                            </Tag>
                                        )}
                                        {filters.performanceBand && (
                                            <Tag closable onClose={() => setFilters(prev => ({ ...prev, performanceBand: '' }))} className="bg-red-50" style={{ color: '#AD0101', borderColor: '#AD0101' }}>
                                                Performance: {filters.performanceBand}
                                            </Tag>
                                        )}
                                        {filters.userType && (
                                            <Tag closable onClose={() => setFilters(prev => ({ ...prev, userTypeId: null, userType: '' }))} className="bg-red-50" style={{ color: '#AD0101', borderColor: '#AD0101' }}>
                                                User Type: {filters.userType}
                                            </Tag>
                                        )}
                                    </div>
                                </div>
                            )}
                        </Card>

                        {/* Right Panel - Users */}
                        <Card
                            title={
                                <span className="flex items-center gap-2">
                                    <Users size={18} />
                                    Users ({selectedUserIds.length} selected of {totalUsersCount})
                                </span>
                            }
                            className="h-full"
                            extra={
                                <div className="flex gap-2">
                                    <button
                                        onClick={selectAllUsers}
                                        className="text-xs text-[#AD0101]"
                                    >
                                        Select All
                                    </button>
                                    <span className="text-gray-300">|</span>
                                    <button
                                        onClick={deselectAllUsers}
                                        className="text-xs text-gray-600"
                                    >
                                        Deselect All
                                    </button>
                                </div>
                            }
                        >
                            {/* Search Users */}
                            <Input
                                placeholder="Search users by name..."
                                prefix={<Search size={16} className="text-gray-400" />}
                                value={searchName}
                                onChange={e => setSearchName(e.target.value)}
                                allowClear
                                className="mb-4"
                            />

                            {/* Users List */}
                            <div className="max-h-[400px] overflow-y-auto space-y-2" onScroll={handleUsersScroll}>
                                {users.length === 0 ? (
                                    <Empty description={usersLoading ? "Loading users..." : "No users match the selected filters"} />
                                ) : (
                                    users.map(user => {
                                        const isSelected = selectedUserIds.includes(user.id);
                                        const isDisabled = !user.profileActivated;
                                        const performance = user.performanceResidency?.performance || '';

                                        return (
                                            <div
                                                key={user.id}
                                                onClick={() => !isDisabled && toggleUser(user.id)}
                                                className={`p-3 rounded-lg border-2 transition ${isDisabled
                                                    ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-60'
                                                    : isSelected
                                                        ? 'border-[#AD0101] bg-red-50 cursor-pointer'
                                                        : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                                                    }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Checkbox
                                                        className="custom-red-checkbox"
                                                        checked={isSelected}
                                                        disabled={isDisabled}
                                                        onChange={() => !isDisabled && toggleUser(user.id)}
                                                    />
                                                    <div className="flex-1">
                                                        <div className="font-medium">{user.fullname}</div>
                                                        {/* <div className="text-xs text-gray-500">{user.email}</div> */}
                                                    </div>
                                                    <div className="flex gap-1">
                                                        {performance && (
                                                            <Tag color={performance === 'HIGH' ? 'green' : performance === 'MEDIUM' ? 'orange' : 'red'} className="text-xs">
                                                                {perfMap[performance] || performance}
                                                            </Tag>
                                                        )}
                                                        {isDisabled && (
                                                            <Tag color="red" className="text-xs">Not Activated</Tag>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                {loadingMoreUsers && (
                                    <div className="flex justify-center py-2">
                                        <Spin size="small" indicator={<LoadingOutlined style={{ color: '#AD0101' }} spin />} />
                                    </div>
                                )}
                                {!loadingMoreUsers && users.length > 0 && !pageMeta?.hasNextPage && (
                                    <div className="text-center text-xs text-gray-400 py-2">All users loaded</div>
                                )}
                            </div>
                        </Card>
                    </div>

                    {/* Footer Actions */}
                    {createdCohortId && (
                        <div className="flex justify-center mt-4 mb-2">
                            <button
                                onClick={() => handleDownloadCohortCSV(createdCohortId)}
                                disabled={exportCohortLoading}
                                className="flex items-center justify-center gap-2 px-6 py-2 rounded-md text-white font-medium transition"
                                style={{
                                    backgroundColor: exportCohortLoading ? '#999' : '#AD0101',
                                    cursor: exportCohortLoading ? 'not-allowed' : 'pointer',
                                    border: 'none',
                                    fontSize: '14px',
                                }}
                            >
                                {exportCohortLoading
                                    ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                    : 'Download CSV'}
                            </button>
                        </div>
                    )}
                    <div className="flex gap-4 mt-6">
                        <button
                            onClick={() => { onCancel(); resetForm(); }}
                            className="flex-1 h-10 rounded-md border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition"
                        >
                            {createdCohortId ? 'Close' : 'Cancel'}
                        </button>
                        {!createdCohortId && (
                            <button
                                onClick={handleSaveCohort}
                                disabled={creatingCohort || !cohortName.trim() || selectedUserIds.length === 0}
                                className={`flex-1 h-10 rounded-md text-white font-medium transition flex items-center justify-center gap-2 ${creatingCohort || !cohortName.trim() || selectedUserIds.length === 0
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-[#AD0101] hover:bg-[#b00000]'
                                    }`}
                            >
                                {creatingCohort ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : `Create Cohort (${selectedUserIds.length} users)`}
                            </button>
                        )}
                    </div>
                </Spin>
            )}
        </Modal>
    );
};

export default ClanCrewCreateCohortModal;